// @flow

// PinRepPresentation acts like a light template for Pin content. No logic
// should be placed in this file, nor should PinRepPresentation depend on any
// other components.

import React, { type Node } from 'react';
import ContentLayer from 'app/common/react/components/PinRep/components/ContentLayer';
import FadeableContainer from 'app/common/react/components/PinRep/components/FadeableContainer';
import PinRepSelectionBorder from 'app/common/react/components/PinRep/components/PinRepSelectionBorder';
import { Box } from 'gestalt';
import { type ContentVisibility } from 'app/common/react/components/PinRep/components/ContentVisibility';
import { type SelectionState } from 'app/common/react/components/PinRep/types';

export const PIN_BORDER_RADIUS = 16;
export const OVERLAYS = {
  SAVED: 'rgba(0, 0, 0, 0.33)',
  VISUAL: 'rgba(0, 0, 0, 0.1)',
};

const styles = {
  wrapper: {
    borderRadius: PIN_BORDER_RADIUS,
    WebkitMaskImage: '-webkit-radial-gradient(white, black)', // necessary for Safari to respect border-radius: https://gist.github.com/ayamflow/b602ab436ac9f05660d9c15190f4fd7b#file-gistfile1-css
  },
  promotedPinBorderTreatment: {
    background: '#EFEFEF',
    padding: 4,
    borderRadius: 20,
  },
  primaryContentContainer: {
    marginBottom: 'auto',
  },
  visualOverlay: { backgroundColor: OVERLAYS.VISUAL },
  savedOverlay: { backgroundColor: OVERLAYS.SAVED },
};

// Props which are calculated in PinRepConfiguration and passed down to PinRepPresentation
export type PresentationProps = {|
  showPromotedPinBorderTreatment: boolean,
|};

type Props = {|
  // Main Visual Content
  children: Node,

  // Overlay
  savedOverlayVisibility: ContentVisibility,
  visualOverlayVisibility: ContentVisibility,

  // Slots and Slot Visibility
  primaryContent?: Node,
  primaryContentVisibility: ContentVisibility,
  secondaryContent?: Node,
  secondaryContentVisibility: ContentVisibility,
  accessoryContent?: Node,
  accessoryContentVisibility?: ContentVisibility,
  actionBar?: Node,
  actionBarVisibility?: ContentVisibility,
  footer?: Node,
  debugContent?: Node,
  signalDebuggerContent?: Node,
  obscuredPinOverlay?: Node,

  // Does this Pin support selection? Is it selected?
  selectionState?: SelectionState,
  ...PresentationProps,
|};

export default function PinRepPresentation({
  children,
  savedOverlayVisibility = 'none',
  visualOverlayVisibility = 'none',
  primaryContent,
  primaryContentVisibility = 'none',
  secondaryContent,
  secondaryContentVisibility = 'none',
  accessoryContent,
  accessoryContentVisibility = 'none',
  actionBar,
  actionBarVisibility = 'none',
  footer,
  selectionState = 'no-selection',
  debugContent,
  signalDebuggerContent,
  obscuredPinOverlay,
  showPromotedPinBorderTreatment,
}: Props) {
  return (
    <Box
      dangerouslySetInlineStyle={{
        __style: showPromotedPinBorderTreatment ? styles.promotedPinBorderTreatment : {},
      }}
    >
      <Box
        dangerouslySetInlineStyle={{
          __style: styles.wrapper,
        }}
        data-test-id="pinWrapper"
        overflow="hidden"
        position="relative"
      >
        {/* Selection Border */}
        {selectionState !== 'no-selection' && (
          <PinRepSelectionBorder selected={selectionState === 'selected'} />
        )}

        <Box
          dangerouslySetInlineStyle={{
            __style: styles.wrapper,
          }}
          overflow="hidden"
          position="relative"
        >
          {/* Link and visuals (image, video, etc) */}
          {children}

          {/* Visual Overlay */}
          <FadeableContainer visibility={visualOverlayVisibility}>
            <ContentLayer additionalStyles={styles.visualOverlay} />
          </FadeableContainer>

          {/* Save Overlay */}
          <FadeableContainer visibility={savedOverlayVisibility}>
            <ContentLayer additionalStyles={styles.savedOverlay} />
          </FadeableContainer>

          {/* Accessory Content */}
          {accessoryContent && (
            <FadeableContainer visibility={accessoryContentVisibility}>
              <ContentLayer>{accessoryContent}</ContentLayer>
            </FadeableContainer>
          )}

          {/* Secondary Content */}
          {secondaryContent && (
            <FadeableContainer visibility={secondaryContentVisibility}>
              <ContentLayer>{secondaryContent}</ContentLayer>
            </FadeableContainer>
          )}

          {/* Primary Content */}
          {primaryContent && (
            <FadeableContainer visibility={primaryContentVisibility}>
              <ContentLayer>{primaryContent}</ContentLayer>
            </FadeableContainer>
          )}

          {/* Action Bar Content */}
          {actionBar && (
            <FadeableContainer visibility={actionBarVisibility}>
              <ContentLayer>
                <Box position="absolute" left right bottom>
                  {actionBar}
                </Box>
              </ContentLayer>
            </FadeableContainer>
          )}

          {/* Debug Content */}
          {debugContent && <ContentLayer>{debugContent}</ContentLayer>}

          {/* Signal Debugger Content */}
          {signalDebuggerContent}
        </Box>

        {/* Footer */}
        {footer}

        {obscuredPinOverlay && <Box>{obscuredPinOverlay}</Box>}
      </Box>
    </Box>
  );
}
